import React from 'react';
import { t } from '../../utils/translations';
import { getFormattedUserName } from '../../utils/cvUtils';

export default function CVTemplateATSSimple({ user, projects, language = 'id' }) {

  // Helper function to categorize education
  const categorizeEducation = (education) => {
    const higher = [];
    const k12 = [];
    
    education.forEach(edu => {
      const degree = edu.degree?.toLowerCase() || '';
      const isHigherEd = degree.includes('sarjana') || degree.includes('s1') || 
                        degree.includes('s2') || degree.includes('s3') || 
                        degree.includes('bachelor') || degree.includes('master') || 
                        degree.includes('magister') || degree.includes('phd') || 
                        degree.includes('doktor') || degree.includes('diploma');
      
      if (isHigherEd) {
        higher.push(edu);
      } else {
        k12.push(edu);
      }
    });
    
    return { higher, k12 };
  };

  const { higher: higherEducation, k12: k12Education } = user?.education ? 
    categorizeEducation(user.education) : { higher: [], k12: [] };

  return (
    <div className="cv-template avoid-break bg-white p-4 sm:p-6 md:p-8 max-w-[21cm] mx-auto print:p-8 print:shadow-none print:max-w-full" style={{ fontFamily: 'Calibri, Arial, sans-serif' }}>
      {/* Header - No Photo, ATS Optimized */}
      <div className="cv-section text-center mb-4 sm:mb-6 pb-3 sm:pb-4 border-b-2 border-black avoid-break">
        <h1 className="text-2xl sm:text-3xl font-bold text-black uppercase mb-1 sm:mb-2">{getFormattedUserName(user) || 'YOUR NAME'}</h1>
        <p className="text-sm sm:text-base text-black mb-1 sm:mb-2 text-center">{user?.job || 'Professional Title'}</p>
        <div className="text-xs sm:text-sm text-black">
          {user?.email && <span>{user.email}</span>}
          {user?.phone && <span> • {user.phone}</span>}
          {user?.location && <span> • {user.location}</span>}
        </div>
      </div>

      {/* Professional Summary */}
      {user?.summary && (
        <div className="cv-section mb-4 sm:mb-5 avoid-break">
          <h2 className="cv-section-title text-sm sm:text-base font-bold text-black uppercase mb-1 sm:mb-2 pb-1 border-b border-black">
            {t('professionalSummary', language)}
          </h2>
          <p className="text-xs sm:text-sm text-black leading-relaxed text-justify">{user.summary}</p>
        </div>
      )}

      {/* Skills */}
      {user?.skills && user.skills.length > 0 && (
        <div className="cv-section mb-4 sm:mb-5 avoid-break">
          <h2 className="cv-section-title text-sm sm:text-base font-bold text-black uppercase mb-1 sm:mb-2 pb-1 border-b border-black">
            {t('skills', language)}
          </h2>
          <p className="text-xs sm:text-sm text-black">
            {user.skills.join(' • ')}
          </p>
        </div>
      )}

      {/* Work Experience */}
      {user?.experience && user.experience.length > 0 && (
        <div className="cv-section mb-4 sm:mb-5 avoid-break">
          <h2 className="cv-section-title text-sm sm:text-base font-bold text-black uppercase mb-1 sm:mb-2 pb-1 border-b border-black">
            {t('workExperience', language)}
          </h2>
          <div className="space-y-2 sm:space-y-3">
            {user.experience.map((exp, i) => (
              <div key={i} className="cv-experience-item avoid-break">
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-baseline">
                  <h3 className="text-xs sm:text-sm font-bold text-black">{exp.position}</h3>
                  <span className="text-xs sm:text-sm text-black">{exp.period}</span>
                </div>
                <p className="text-xs sm:text-sm text-black font-semibold">{exp.company}</p>
                {exp.description && (
                  <p className="text-xs sm:text-sm text-black mt-1 text-justify">{exp.description}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Education - Higher Education (Kuliah) */}
      {higherEducation.length > 0 && (
        <div className="cv-section mb-4 sm:mb-5 avoid-break">
          <h2 className="cv-section-title text-sm sm:text-base font-bold text-black uppercase mb-1 sm:mb-2 pb-1 border-b border-black">
            {t('higherEducation', language)}
          </h2>
          <div className="space-y-2">
            {higherEducation.map((edu, i) => (
              <div key={i} className="cv-education-item avoid-break">
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-baseline">
                  <h3 className="text-sm font-bold text-black">{edu.degree}</h3>
                  <span className="text-sm text-black">{edu.year}</span>
                </div>
                <p className="text-sm text-black">{edu.school || edu.institution}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Education - K-12 (Sekolah) */}
      {k12Education.length > 0 && (
        <div className="cv-section mb-4 sm:mb-5 avoid-break">
          <h2 className="cv-section-title text-sm sm:text-base font-bold text-black uppercase mb-1 sm:mb-2 pb-1 border-b border-black">
            {t('formalEducation', language)}
          </h2>
          <div className="space-y-2">
            {k12Education.map((edu, i) => (
              <div key={i} className="cv-education-item avoid-break">
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-baseline">
                  <h3 className="text-xs sm:text-sm font-bold text-black">{edu.degree}</h3>
                  <span className="text-xs sm:text-sm text-black">{edu.year}</span>
                </div>
                <p className="text-xs sm:text-sm text-black">{edu.school || edu.institution}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Projects */}
      {projects && projects.length > 0 && (
        <div className="cv-section mb-4 sm:mb-5 avoid-break">
          <h2 className="cv-section-title text-sm sm:text-base font-bold text-black uppercase mb-1 sm:mb-2 pb-1 border-b border-black">
            {t('projects', language)}
          </h2>
          <div className="space-y-2">
            {projects.map((proj, i) => (
              <div key={i} className="cv-project-item avoid-break">
                <h3 className="text-xs sm:text-sm font-bold text-black">{proj.title}</h3>
                <p className="text-xs sm:text-sm text-black text-justify">{proj.description}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
